/**
 * FlzMessageList — Virtual scroll ile yüksek performanslı mesaj listesi
 * Twitter gibi: DOM'da sadece görünen mesajlar render edilir
 * Yukarı scroll'da otomatik eski mesaj yükleme
 */
import { useVirtualizer } from '@tanstack/react-virtual';
import { Send } from 'lucide-react';
import React, { useCallback, useEffect, useRef } from 'react';
import { Message } from '../../../types/flzhub';
import GazzaliVirtueIcons from '../../esma/GazzaliVirtueIcons';
import { Badge, getAgentColor, getAgentInitials, getRoleBadgeColor, getRoleTitle } from './Shared';

function formatTime(ts: string | null | undefined): string {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

interface Props {
  messages: Message[];
  input: string;
  setInput: (v: string) => void;
  sendMessage: () => void;
  sending: boolean;
  isAdmin?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  broadcastMode?: boolean;
  setBroadcastMode?: (v: boolean) => void;
}

export const FlzMessageList: React.FC<Props> = ({
  messages, input, setInput, sendMessage, sending,
  isAdmin = false, onLoadMore, hasMore = false, loadingMore = false,
  broadcastMode = false, setBroadcastMode,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const isFirstLoad = useRef(true);

  // Messages come DESC from backend — reverse to show oldest at top, newest at bottom
  const sorted = [...messages].reverse();

  const virtualizer = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72, // ortalama mesaj yüksekliği
    overscan: 10, // ekranın dışında kaç item render edilsin
  });

  // İlk yüklemede en alta scroll
  useEffect(() => {
    if (isFirstLoad.current && sorted.length > 0) {
      virtualizer.scrollToIndex(sorted.length - 1, { behavior: 'auto' });
      isFirstLoad.current = false;
    }
  }, [sorted.length]);

  // Yeni mesaj gelince en alta scroll
  const prevLengthRef = useRef(0);
  useEffect(() => {
    if (sorted.length > prevLengthRef.current && !isFirstLoad.current) {
      // Sadece yeni mesaj eklenince (load more değil) en alta git
      if (sorted.length - prevLengthRef.current <= 5) {
        virtualizer.scrollToIndex(sorted.length - 1, { behavior: 'smooth' });
      }
    }
    prevLengthRef.current = sorted.length;
  }, [sorted.length]);

  // Yukarı scroll'da eski mesaj yükle
  const handleScroll = useCallback(() => {
    if (!parentRef.current || !hasMore || loadingMore) return;
    if (parentRef.current.scrollTop < 100) {
      onLoadMore?.();
    }
  }, [hasMore, loadingMore, onLoadMore]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
      {/* Scroll container */}
      <div
        ref={parentRef}
        onScroll={handleScroll}
        style={{ flex: 1, overflowY: 'auto', position: 'relative' }}
      >
        {/* Loading indicator at top */}
        {loadingMore && (
          <div style={{ textAlign: 'center', fontSize: 10, color: 'var(--os-muted)', padding: 8 }}>
            Loading older messages...
          </div>
        )}
        {hasMore && !loadingMore && (
          <button
            onClick={onLoadMore}
            style={{ display: 'block', width: '100%', background: 'none', border: 'none', color: 'var(--os-accent)', fontSize: 10, cursor: 'pointer', padding: 8, textAlign: 'center' }}
          >
            Load older messages
          </button>
        )}

        {/* Virtual list container */}
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map(virtualItem => {
            const m = sorted[virtualItem.index];
            if (!m) return null;
            const isAdminMsg = m.message_type === 'admin_message' || m.message_type === 'admin_broadcast';
            const isTrainerMsg = m.message_type === 'trainer_message';
            const color = isAdminMsg ? 'var(--os-warning)' : isTrainerMsg ? '#10b981' : getAgentColor(m.agent_name);

            return (
              <div
                key={m.id}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                  padding: '8px 16px',
                }}
              >
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', background: color, color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 2,
                  }}>
                    {isAdminMsg ? 'A' : isTrainerMsg ? 'T' : getAgentInitials(m.agent_name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color }}>
                        {isAdminMsg ? 'System Admin' : isTrainerMsg ? 'Trainer' : m.agent_name}
                      </span>
                      {!isAdminMsg && <Badge color={getRoleBadgeColor(m.agent_id)}>{getRoleTitle(m.agent_id)}</Badge>}
                      {m.eq_score != null && (
                        <span style={{ fontSize: 9, color: m.eq_score >= 8 ? '#10b981' : '#818cf8', fontWeight: 600 }}>
                          EQ {m.eq_score.toFixed(1)}
                        </span>
                      )}
                      {m.gazzali_scores && <GazzaliVirtueIcons gazzali_scores={m.gazzali_scores} size={10} />}
                      <span style={{ fontSize: 9, color: 'var(--os-dim)', marginLeft: 'auto' }}>
                        {formatTime(m.created_at)}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--os-text)' }}>
                      {m.message}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Input area */}
      <div style={{ padding: 12, borderTop: '1px solid var(--os-border)', display: 'flex', gap: 10, background: 'var(--os-surface)', flexShrink: 0 }}>
        {isAdmin ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
            {/* Broadcast mode toggle */}
            {setBroadcastMode && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => setBroadcastMode(!broadcastMode)}
                  style={{
                    padding: '3px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 9, fontWeight: 700,
                    background: broadcastMode ? 'var(--os-warning)' : 'var(--os-hover)',
                    color: broadcastMode ? '#000' : 'var(--os-muted)',
                  }}
                >
                  {broadcastMode ? '📢 Broadcast Mode ON' : '💬 Normal Message'}
                </button>
                {broadcastMode && (
                  <span style={{ fontSize: 9, color: 'var(--os-warning)' }}>
                    Anne, Baba & 3 Experts will respond
                  </span>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                placeholder={broadcastMode ? "Start a discussion topic for the family..." : "Send a message to the family..."}
                style={{
                  flex: 1, background: broadcastMode ? 'var(--os-warning-bg)' : 'var(--os-hover)',
                  border: `1px solid ${broadcastMode ? 'var(--os-warning)' : 'var(--os-border)'}`,
                  color: 'var(--os-text)', padding: '10px 16px', borderRadius: 10, fontSize: 11, outline: 'none',
                }}
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                style={{
                  background: broadcastMode ? 'var(--os-warning)' : 'var(--os-accent)', border: 'none', color: '#fff', width: 40, height: 40,
                  borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', opacity: sending ? 0.5 : 1, flexShrink: 0,
                }}
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flex: 1 }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendMessage()}
              placeholder="Write a message..."
              style={{
                flex: 1, background: 'var(--os-hover)', border: '1px solid var(--os-border)',
                color: 'var(--os-text)', padding: '10px 16px', borderRadius: 10, fontSize: 11, outline: 'none',
              }}
            />
            <button
              onClick={sendMessage}
              disabled={sending || !input.trim()}
              style={{ background: 'var(--os-accent)', border: 'none', color: '#fff', width: 40, height: 40, borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: sending ? 0.5 : 1, flexShrink: 0 }}
            >
              <Send size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
